/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import { useMemo } from 'react';

import { useCollectionSurtitle } from '../../hooks/useSurtitle';

import { useDefaultBrand } from '../../contexts/SiteContext';
import Blocks from '../blocks/Blocks';
import CollectionSerieHeader from '../headers/CollectionSerieHeader';
import InfiniteItemsBlocks from '../lists/InfiniteItemsBlocks';
import DocumentTheme from '../themes/DocumentTheme';
import SectionTitle from '../typography/SectionTitle';

import styles from '../../styles/collections/serie-collection.module.css';

import serieBackground from '../../assets/img/serie_background.png';

interface SerieCollectionProps {
    slug: string;
    id?: string | null;
    type?: string | null;
    surtitle?: string | null;
    title?: string | null;
    description?: string | null;
    header?: CollectionHeader | null;
    metadata?: CollectionMetadata | null;
    theme?: Theme | null;
    image?: Image | null;
    seasons?: CollectionSnippet[] | null;
    children?: CollectionSnippet[] | null;
    currentDocument?: Document | null;
    current?: boolean;
    withoutHeaderImage?: boolean;
    className?: string;
}

function SerieCollection({
    id: collectionId = null,
    slug = null,
    type = null,
    surtitle = null,
    title = null,
    description = null,
    header = null,
    metadata,
    theme,
    image,
    seasons = null,
    children = null,
    currentDocument = null,
    current = false,
    withoutHeaderImage = false,
    className,
}: SerieCollectionProps) {
    const { brands } = metadata || {};
    const { name: themeName = null, backgroundColor = null } = theme || {};
    const defaultBrand = useDefaultBrand();
    const { handle: defaultBrandHandle = 'urbania' } = defaultBrand || {};
    const [brand = null] = brands || [];
    const { handle: brandHandle = defaultBrandHandle } = brand || {};

    const finalSurtitle = useCollectionSurtitle({ type, surtitle, metadata, seasons });

    const isPodcast = type === 'serie_podcast' || type === 'season_podcast';
    const hasSeasons = seasons !== null && seasons.length > 0;
    const hasChildren = children !== null && children.length > 0;

    const childrenBlocks = useMemo(() => {
        if (hasSeasons) {
            return seasons.map(({ id: seasonId, title: seasonTitle }) => ({
                type: 'documents-row',
                presentation: 'grid',
                title: <SectionTitle>{seasonTitle}</SectionTitle>,

                cardWithout: ['brand-icon', 'serie', 'collection', 'category', 'time'],
                query: {
                    collection: seasonId,
                },
                loading: 'lazy',

                count: null,
                placeholderCount: 4,
                // minColumns: 1,
                ...(isPodcast
                    ? {
                          cardType: 'horizontal',
                          cardTheme: 'box-translucent-dark',
                          cardRatio: 'square',
                          cardSize: 'medium',
                          columns: 1,
                      }
                    : {
                          //   cardType: 'over',
                          //   cardSize: 'big',
                          //   cardTheme: 'box-translucent',
                          cardTheme: 'rounded',
                          cardRatio: 'horizontal',
                          cardWidth: 300,
                      }),
            }));
        }
        return (children || []).map(({ id: childId, slug: childSlug, ...child }) => ({
            type: 'collection-row',
            presentation: 'grid',
            slug: childSlug,

            cardWithout: ['brand-icon', 'serie', 'collection', 'category'],
            withoutSummarySponsor: true,
            summaryImagePosition: 'left',
            // query: {
            //     collection: childId,
            // },
            loading: 'lazy',
        }));
    }, [hasSeasons, seasons, children]);

    return (
        <DocumentTheme
            id={`collection-${collectionId}`}
            backgroundImage={
                backgroundColor !== null
                    ? {
                          url: serieBackground,
                      }
                    : null
            }
            {...theme}
            containerClassName={styles.container}
            className={classNames([
                styles.container,
                { [styles.withDocument]: currentDocument !== null },
                className,
            ])}
            data-brand={brandHandle}
            data-theme={themeName}
        >
            <CollectionSerieHeader
                type={type}
                title={title}
                surtitle={finalSurtitle}
                description={description}
                metadata={metadata}
                background={
                    type === 'magazine'
                        ? {
                              color: '#fff',
                          }
                        : null
                }
                {...header}
                image={header !== null ? header.image || image : image}
                withoutImage={withoutHeaderImage}
                placeholder={collectionId === null}
                currentDocument={currentDocument}
                className={styles.header}
                imageClassName={styles.headerImage}
            />
            <main className={styles.content}>
                {childrenBlocks !== null && childrenBlocks.length > 0 ? (
                    <Blocks
                        blocks={childrenBlocks}
                        className={styles.blocks}
                        // withoutMaxWidth
                        blockClassName={classNames([
                            styles.row,
                            {
                                [styles.season]: hasSeasons,
                                [styles.podcast]: isPodcast,
                            },
                        ])}
                    />
                ) : null}
                {!hasChildren ? (
                    <InfiniteItemsBlocks
                        query={
                            hasSeasons
                                ? {
                                      collection: collectionId,
                                      exclude_type: isPodcast ? 'podcast' : 'video',
                                  }
                                : {
                                      collection: collectionId,
                                  }
                        }
                        itemsBlocks={[
                            {
                                type: 'items',
                                itemsType: 'documents',
                                presentation: 'grid',
                                cardWidth: 220,
                                cardTheme: 'rounded',
                                cardRatio: 'horizontal',
                                cardSize: 'responsive',
                                count: 12,
                                minColumns: 2,
                            },
                        ]}
                        disabled={collectionId === null}
                        className={styles.blocks}
                        // withoutMaxWidth
                        cardTheme="rounded"
                        cardWithout={['brand-icon', 'serie', 'collection', 'time']}
                    />
                ) : null}
            </main>
        </DocumentTheme>
    );
}

export default SerieCollection;
